import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useNavigate } from 'react-router-dom';
import { FinancialDocument } from 'src/features/financialDocuments/utils/types';
import { useDeleteFinancialDocument } from 'src/features/financialDocuments/api/useDeleteFinancialDocument';
import { LIST_FINANCIAL_DOCUMENTS_RESOURCE } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { useOnError } from 'src/utils/errors';

interface DeleteFinancialDocumentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedFinancialDocument: FinancialDocument;
    children?: React.ReactNode;
}

function DeleteFinancialDocumentFormModal<TData>({
    selectedFinancialDocument,
    children,
}: DeleteFinancialDocumentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const onError = useOnError();

    const { mutate: deleteFinancialDocument } = useDeleteFinancialDocument({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENTS_RESOURCE] });
            navigate(`/financial-documents`);
            setIsModalOpen(false);
            toast({
                title: formatMessage(financialDocumentsMessages.deleteFinancialDocumentSuccess),
                description: formatMessage(financialDocumentsMessages.deleteFinancialDocumentSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...financialDocumentsMessages.deleteFinancialDocument },
                subTitle: {
                    ...financialDocumentsMessages.deleteFinancialDocumentSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...financialDocumentsMessages.confirmDeleteFinancialDocument },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: financialDocumentsMessages.deleteFinancialDocument,
                callbackFn: () => {
                    return (
                        deleteFinancialDocument({
                            documentId: selectedFinancialDocument.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteFinancialDocumentFormModal;
